<template>
  <button
    type="button"
    class="user-menu-container button-reset"
    aria-label="Open user profile menu"
    @click="toggleDropdown"
  >
    <Button
      as="span"
      data-test="user-profile-toggle"
      variant="outlined"
      severity="contrast"
      rounded
      icon="pi pi-user"
    />
    <img src="@/assets/images/elements/triangle_down.svg" class="d-triangle-down" alt="Open drop down menu icon" />
  </button>

  <PrimeMenu
    id="user-menu-overlay"
    ref="menu"
    :model="menuItems"
    :popup="true"
    :pt="{
      root: {
        style: 'position: fixed; top: 4rem; right: 0; z-index: 1000;',
      },
      list: {
        'data-test': 'profileMenu',
      },
      item: ({ context }) => {
        return {
          style: `font-weight: var(--font-weight-semibold); ${context.disabled ? 'display: none' : ''}`,
        };
      },
    }"
  />
</template>

<script setup lang="ts">
import router from '@/router';
import { logoutAndRedirectToUri } from '@/utils/KeycloakUtils';
import { assertDefined } from '@/utils/TypeScriptUtils';
import type Keycloak from 'keycloak-js';
import Button from 'primevue/button';
import PrimeMenu from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import { computed, inject, type Ref, useTemplateRef } from 'vue';

const getKeycloakPromise = inject<() => Promise<Keycloak>>('getKeycloakPromise');
const menu = useTemplateRef('menu');

/**
 * Toggles Menu as Popup
 * @param event
 */
function toggleDropdown(event: Event): void {
  menu?.value?.toggle(event);
}

/**
 * Redirects the user to the keycloak user settings page
 */
function goToUserSettings(): void {
  assertDefined(getKeycloakPromise)()
    .then((keycloak) => keycloak.accountManagement())
    .catch((error) => console.log(error));
}

/**
 * Logs the user out and redirects him to the dataland homepage
 */
function logoutViaDropdown(): void {
  assertDefined(getKeycloakPromise)()
    .then((keycloak) => logoutAndRedirectToUri(keycloak, '/'))
    .catch((error) => console.log(error));
}

const menuItems: Ref<MenuItem[]> = computed(() => [
  {
    label: 'USER SETTINGS',
    icon: 'pi pi-cog',
    id: 'profile-picture-dropdown-settings-button',
    command: goToUserSettings,
  },
  {
    label: 'API KEY',
    icon: 'pi pi-key',
    id: 'profile-api-generate-key-button',
    command: (): void => void router.push('/api-key'),
  },
  {
    label: 'LOG OUT',
    icon: 'pi pi-sign-out',
    id: 'profile-picture-dropdown-logout-anchor',
    command: logoutViaDropdown,
  },
]);
</script>

<style scoped>
.user-menu-container {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  &:hover {
    cursor: pointer;
  }
}

.d-triangle-down {
  width: 0.625rem;
  margin-left: 0.5rem;
  margin-right: 1rem;
}
</style>
